// components/PurchaseModal.tsx - Drop-in component for storage purchases

import React, { useState } from 'react';
import { Crown, Check, Loader, AlertCircle, X } from 'lucide-react';
import { UserProfile } from './services/authService';

interface StoragePackage {
  id: string;
  slots: number;
  pricePerSlot: number;
  totalPrice: number;
  popular?: boolean;
}

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile;
}

const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || '';
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const STORAGE_PACKAGES: StoragePackage[] = [
  {
    id: 'pack_5',
    slots: 5,
    pricePerSlot: 0.40,
    totalPrice: 2.00
  },
  {
    id: 'pack_10',
    slots: 10,
    pricePerSlot: 0.35,
    totalPrice: 3.50,
    popular: true
  },
  {
    id: 'pack_20',
    slots: 20,
    pricePerSlot: 0.30,
    totalPrice: 6.00
  },
  {
    id: 'pack_30',
    slots: 30,
    pricePerSlot: 0.27,
    totalPrice: 8.00
  }
];

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  userProfile
}) => {
  const [selectedPackage, setSelectedPackage] = useState<StoragePackage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setLoading(true);
    setError(null);

    try {
      // Get Firebase ID token
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Not authenticated');
      }
      if (!FUNCTIONS_URL) {
        throw new Error('Payment endpoint is not configured');
      }
      if (!STRIPE_KEY) {
        throw new Error('Stripe publishable key is not configured');
      }

      const idToken = await user.getIdToken();
      const stripeMode = STRIPE_KEY.startsWith('pk_test_') ? 'test' : 'live';

      console.log('FUNCTIONS_URL', FUNCTIONS_URL);
      console.log('STRIPE_KEY present?', Boolean(STRIPE_KEY));
      console.log('selectedPackage', selectedPackage);
      console.log('currentUser?', auth.currentUser?.uid);


      // Create Stripe Checkout session
      const response = await fetch(`${FUNCTIONS_URL}/createCheckoutSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          packageId: selectedPackage.id,
          mode: stripeMode,
          publishableKey: STRIPE_KEY
        })
      });

      if (!response.ok) {
         const text = await response.text();
        let msg = text;
        try { msg = JSON.parse(text).details || JSON.parse(text).error || text; } catch {}
        throw new Error(`HTTP ${response.status} – ${msg}`);
        }


      const { sessionId } = await response.json();

      // Redirect to Stripe Checkout
      const stripe = (window as any).Stripe(STRIPE_KEY);
      const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });

      if (stripeError) {
        throw new Error(stripeError.message);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err instanceof Error ? err.message : 'Payment failed');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentSlots = userProfile.libraryLimit;
  const usedSlots = userProfile.libraryCount;
  const remainingSlots = currentSlots - usedSlots;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '0.5rem'
          }}
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ 
            display: 'inline-flex', 
            padding: '1rem', 
            backgroundColor: '#fef3c7',
            borderRadius: '50%',
            marginBottom: '1rem'
          }}>
            <Crown size={32} color="#f59e0b" />
          </div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', margin: '0 0 0.5rem 0' }}>
            Get More Storage
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.9375rem' }}>
            Choose a package to add more video slots to your library
          </p>
        </div>

        {/* Current Status */}
        <div style={{
          padding: '1rem',
          backgroundColor: '#f3f4f6',
          borderRadius: '0.75rem',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
              Current Library
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: '500', marginLeft: '2.75rem' }}>
            {usedSlots} / {currentSlots} slots used
            </div>
          </div>
          <div style={{
            padding: '0.5rem 1rem',
            backgroundColor: remainingSlots === 0 ? '#fee2e2' : '#dbeafe',
            color: remainingSlots === 0 ? '#991b1b' : '#1e40af',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}>
            {remainingSlots} remaining
          </div>
        </div>

        {/* Packages */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 2fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {STORAGE_PACKAGES.map((pkg, index) => {
            // Calculate savings vs smallest package
            const smallestPrice = STORAGE_PACKAGES[0].pricePerSlot;
            const savingsPerSlot = smallestPrice - pkg.pricePerSlot;
            const totalSavings = savingsPerSlot * pkg.slots;
            const showSavings = savingsPerSlot > 0;
            
            return (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                disabled={loading}
                style={{
                  position: 'relative',
                  padding: '1.25rem',
                  border: selectedPackage?.id === pkg.id 
                    ? '2px solid #2563eb' 
                    : '2px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  backgroundColor: selectedPackage?.id === pkg.id ? '#eff6ff' : 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (selectedPackage?.id !== pkg.id && !loading) {
                    e.currentTarget.style.borderColor = '#cbd5e1';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedPackage?.id !== pkg.id) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '-15px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    Popular
                  </div>
                )}
                
                {showSavings && (
                  <div style={{
                    position: 'absolute',
                    top:  '150px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    borderRadius: '9999px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    Save €{totalSavings.toFixed(2)}
                  </div>
                )}
                
                <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.25rem', marginTop: showSavings ? '0.5rem' : '0' }}>
                  +{pkg.slots}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                  video slots
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#2563eb' }}>
                  €{pkg.totalPrice.toFixed(2)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                  €{pkg.pricePerSlot.toFixed(2)}/slot/year
                </div>
                
                {index === STORAGE_PACKAGES.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    top: '-15px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    Best Value
                  </div>
                )}
                
                {selectedPackage?.id === pkg.id && (
                  <div style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '0.5rem',
                    width: '1.5rem',
                    height: '1.5rem',
                    backgroundColor: '#2563eb',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Check size={16} color="white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Summary */}
        {selectedPackage && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f0fdf4',
            borderRadius: '0.75rem',
            marginBottom: '1.5rem',
            border: '1px solid #bbf7d0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#15803d', fontSize: '0.9375rem' }}>New total capacity:</span>
              <span style={{ fontWeight: '700', color: '#15803d' }}>
                {currentSlots + selectedPackage.slots} slots
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: '#166534' }}>
              <span>Valid for:</span>
              <span style={{ fontWeight: '600' }}>1 year from purchase</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '0.75rem',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.875rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.9375rem',
              fontWeight: '500',
              opacity: loading ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handlePurchase}
            disabled={!selectedPackage || loading}
            style={{
              flex: 2,
              padding: '0.875rem',
              backgroundColor: !selectedPackage || loading ? '#d1d5db' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: !selectedPackage || loading ? 'not-allowed' : 'pointer',
              fontSize: '0.9375rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Processing...
              </>
            ) : (
              <>Continue to Payment</>
            )}
          </button>
        </div>

        {/* Info */}
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#fef3c7',
          borderRadius: '0.5rem',
          fontSize: '0.8125rem',
          color: '#92400e'
        }}>
          <strong>ℹ️ How it works:</strong>
          <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
            <li>Storage expires 1 year after purchase</li>
            <li>Videos uploaded during the year remain accessible</li>
            <li>Secure payment via Stripe</li>
            <li>Instant activation after payment</li>
          </ul>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PurchaseModal;
